import Card from '../../../Atoms/Card'
import { useUserStore } from '../../../Stores/UserStore/UserStore'
import { PromotionRibbon } from '../../PromotionRibbon/PromotionRibbon'
import AddToCart from '../Shared/AddToCart'
import AddToList from '../Shared/AddToList'
import Fitment from '../Shared/Fitment'
import Promo from '../Shared/Promo'
import ShippingInfo from '../Shared/ShippingInfo'
import styles from './VerticalProductCard.module.scss'

import React, { useEffect } from 'react'

import AppName from 'AppName'
import FeatureFlagWrapper from 'UtilityComponents/FeatureFlagWrapper'
import ClassNames from 'classnames'
import Image from 'next/image'

import { getSuperMarketAffinity } from 'Utilities/Instrumentation/SegmentUtilities/segmentFunctions'

import { useSkuBasePromoData } from 'Contexts/UserSpecificProductDataStore/UserSpecificProductDataStore'

import { useLazyFeatureFlag } from 'Hooks/useFeatureFlag/useFeatureFlag'

import Button from 'Molecules/Button'
import Share from 'Molecules/Share'

import Rating from 'Atoms/Rating'
import Svg from 'Atoms/Svg'
import Typography from 'Atoms/Typography'

const VerticalProductCard = ({
  product,
  fitmentTargetLabel,
  page,
  search, // todo: remove
  shop, // todo: remove
  provider, // todo: remove
  misoId,
  imageLoadingProps = null,
  classNames,
  addToCartEnabled,
  shippingEstimatesEnabled,
  addToListEnabled,
  addToListExpanded,
  compareToolEnabled,
  openCompareTool,
  useReducedImage = false,
  onClick,
  onPointerDown,
  index,
  fitmentSectionInteractive,
  selectedFacetOptions,
}) => {
  const showReviewLabelRef = React.useRef(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      showReviewLabelRef.current = window.innerWidth > 384
    }
  }, [])

  const isInternational = useUserStore((x) => x.context.isInternational)
  const promoData = useSkuBasePromoData(product.skuBaseNumber)
  const isMadeInUsa = useLazyFeatureFlag('PRP_Made_In_Usa_Enabled', product.isMadeInUsa && isInternational == false)

  if (!page) {
    console.error(
      "The page prop is required for the EnhancedProductCard.Vertical component. This is needed for unique GA events. Examples are 'prp', 'cart', etc.",
    )
    return false
  }

  if (!product) {
    console.error('The product prop is required for the vertical product card.')
    return false
  }

  if (isMadeInUsa) {
    console.log('Made In USA shown')
  }

  const hasActiveRebate = product.hasActiveRebate
  const percentOffString = product.pricing?.percentOffString

  let ribbonText = product.ribbonText
  if (ribbonText == null)
    if (hasActiveRebate) ribbonText = 'Rebate'
    else ribbonText = percentOffString

  const lowPriceString = promoData?.priceAfterPromo?.listPrice?.string || product?.pricing?.salePriceString

  return (
    <Card
      fill
      fillHeight
      href={product.productPageUrl}
      {...(onClick ? { onClick: onClick } : {})}
      onPointerDown={onPointerDown}
      classNames={ClassNames(classNames, styles.card)}
      noMargin
      segmentEvent={{
        event: 'Product Clicked',
        properties: {
          list_id: 'prp_vertical',
          product_id: product.skuBaseNumber,
          sku: product.skuBaseNumber,
          category: product.segmentCategorization,
          name: product.displayTitle,
          brand: product.brand,
          price: product.pricing?.salePrice,
          position: index,
          url: product.productPageUrl,
          image_url: product.imageLink,
          availability: product.availability,
          rating: product.reviewCount ? product.rating : 0,
          reviews: product.reviewCount,
          audience_affinity: getSuperMarketAffinity(product),
          miso: {
            //todo: remove this after BR vs Miso test
            provider: provider,
            search: search,
            shop: shop,
            miso_id: misoId,
          },
        },
      }}
    >
      <div className={styles.productCardWrapper}>
        <div className={styles.top}>
          <PromotionRibbon
            dataTestId={hasActiveRebate ? 'rebate_ribbon' : 'sale_ribbon'}
            displayText={ribbonText}
            promotionPreview={promoData}
            className={ClassNames(styles.productRibbon, 'product_ribbon')}
            dimensional
          />
          <div className={styles.imageAndTitle}>
            <div className={styles.imageWrapper}>
              {isMadeInUsa ? (
                <div className={styles.madeInUsa}>
                  <Svg icon={'made-usa'} size={3}></Svg>
                </div>
              ) : (
                false
              )}
              <Image
                src={`https:${
                  product?.imageLink ? product?.imageLink : '//content.speedwaymotors.com/OtherImages/missingimage2.jpg'
                }`}
                priority={imageLoadingProps?.priority || false}
                lazyBoundary={imageLoadingProps?.lazyBoundary || '200px'}
                alt={product.displayTitle}
                width={useReducedImage ? 150 : 250}
                height={useReducedImage ? 150 : 250}
              />
            </div>
            <div className={styles.brandAndSku}>
              {product.brand ? (
                <p className="p p-subtle p-0_875" style={{ marginRight: '0.25rem' }}>
                  {product.brand} |
                </p>
              ) : (
                false
              )}
              <Typography size={0.875} tone={'subtle'} href={product.productPageUrl} inline>
                {`#${product.skuBaseNumber}`}
              </Typography>
            </div>

            <div className={styles.title}>
              <Typography
                size={1}
                as={'h3'}
                tone={'contrast'}
                font={'bold'}
                href={product.productPageUrl}
                data-testid={`product_card_title_${product.displayTitle}`}
              >
                {product.displayTitle}
              </Typography>
            </div>
          </div>
          <div className={styles.bottomInfo}>
            <div className={styles.productInfo}>
              <span className={styles.rating}>
                {product?.reviewCount > 0 ? (
                  <>
                    <Rating ratingValue={product.rating} size={1} />
                    <Typography size={0.875} tone={'subtle'} inline className={styles.review_count}>
                      {` (${product?.reviewCount || 0}${showReviewLabelRef?.current ? ' Review' : ''}${
                        showReviewLabelRef?.current && product?.reviewCount > 1 ? 's' : ''
                      })`}
                    </Typography>
                  </>
                ) : (
                  <div className={styles.smallSpacer} />
                )}
              </span>

              {product.pricing ? (
                <>
                  {product.highPrice > product.lowPrice ? (
                    <Typography size={0.875} tone={'subtle'} inline>
                      as low as
                    </Typography>
                  ) : (
                    <div className={styles.smallSpacer} />
                  )}
                  <div className={styles.price}>
                    <div>
                      <span className={styles.priceUnits}>
                        <Typography
                          size={1.25}
                          tone={'contrast'}
                          font={'bold'}
                          inline
                          dataTesting={`regular_price_${product.skuBaseNumber}`}
                        >
                          {lowPriceString}
                        </Typography>
                        {product.unit && (
                          <Typography className={styles.units} size={0.875} tone={'subtle'} inline>
                            {` /${product.unit}`}
                          </Typography>
                        )}
                      </span>
                      {product.pricing.strikeThroughPrice ? (
                        <Typography
                          size={0.875}
                          tone={'subtle'}
                          strikethrough
                          dataTesting={`strikethrough_price_${product.skuBaseNumber}`}
                        >
                          {product.pricing.strikeThroughPrice}
                        </Typography>
                      ) : undefined}
                    </div>
                  </div>
                </>
              ) : undefined}
              <Promo promo={promoData} />

              <Fitment
                skuBaseNumber={product.skuBaseNumber}
                fitmentTargetLabel={fitmentTargetLabel}
                styles={styles}
                fitmentData={product.fitmentData}
                fitmentSectionInteractive={fitmentSectionInteractive}
                selectedFacetOptions={selectedFacetOptions}
              />

              <ShippingInfo skuBaseNumber={product.skuBaseNumber} className={styles.shippingBlock} />
            </div>
          </div>
        </div>
        <div className={styles.ctaSection}>
          {addToCartEnabled !== false && !(product.shipping?.shippingDetailsString === 'Out of Stock') && (
            <div className={styles.callToAction} data-testid={`add_to_cart_prp_${product?.productPageId}`}>
              <AddToCart product={product} hasSpecs={false} />
            </div>
          )}
          {compareToolEnabled && (
            <div
              className={ClassNames(styles.callToAction, `lr_compareTool_${AppName}`)}
              data-testid={`compare_tool_prp_${product?.productPageId}`}
            >
              <Button
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  openCompareTool()
                }}
                intent={'subdued'}
                fill
                segmentEvent={{
                  event: 'Clicked',
                  properties: {
                    element: `Prp - Opened Compare Tool`,
                  },
                }}
                size={'small'}
              >
                <Typography size={0.875}>Compare Similar Items</Typography>
              </Button>
            </div>
          )}
          <FeatureFlagWrapper ff={'Product_Card_Share'}>
            <Share
              className={styles.share}
              url={product.productPageUrl}
              title={product.displayTitle}
              size={1}
              tone={'subtle'}
              subtitle={'Look what I found on Speedway Motors:'}
              data-testid={'prp-share'}
              segmentEvent={{
                event: 'Product Shared',
                properties: {
                  share_via: 'link',
                  product_id: product.skuBaseNumber,
                  sku: product.skuBaseNumber,
                  category: product.segmentCategorization,
                  name: product.displayTitle,
                  brand: product.brand,
                  price: product.pricing?.salePrice,
                  position: index,
                  url: product.productPageUrl,
                  image_url: product.imageLink,
                  availability: product.availability,
                  rating: product.reviewCount ? product.rating : 0,
                  reviews: product.reviewCount,
                  audience_affinity: getSuperMarketAffinity(product),
                },
              }}
            />
          </FeatureFlagWrapper>
          <AddToList
            enabled={addToListEnabled}
            expanded={addToListExpanded}
            skuBaseNumber={product.skuBaseNumber}
            productPageId={product?.productPageId}
          />
        </div>
      </div>
    </Card>
  )
}
export default VerticalProductCard
